/**
 * Role Mapping Utility
 *
 * Maps database role slugs (kebab-case) to ROLES constants (SCREAMING_SNAKE_CASE)
 *
 * Database format: 'compliance-manager', 'project-manager', etc.
 * ROLES constant: 'COMPLIANCE_MANAGER', 'PROJECT_MANAGER', etc.
 */

import { ROLES } from '@config/roles'

/**
 * Role slug to ROLES constant mapping
 * Maps database role_slug values to ROLES constant values
 */
const ROLE_SLUG_MAP = {
	'super-admin': ROLES.SUPER_ADMIN,
	'company-admin': ROLES.COMPANY_ADMIN,
	'compliance-manager': ROLES.COMPLIANCE_MANAGER,
	'project-manager': ROLES.PROJECT_MANAGER,
	'management': ROLES.MANAGEMENT,
	'executive': ROLES.EXECUTIVE,
	'employee': ROLES.EMPLOYEE,
}

/**
 * Convert database role slug to ROLES constant
 *
 * @param {string} roleSlug - Role slug from database (e.g., 'compliance-manager')
 * @returns {string} ROLES constant (e.g., 'COMPLIANCE_MANAGER')
 *
 * @example
 * const userRole = mapRoleSlugToConstant(user.role.slug)
 * // 'compliance-manager' → 'COMPLIANCE_MANAGER'
 */
export const mapRoleSlugToConstant = (roleSlug) => {
	if (!roleSlug) {
		return null
	}

	// Normalize to lowercase
	const normalizedSlug = roleSlug.toLowerCase()

	// Return mapped value or null if not found
	return ROLE_SLUG_MAP[normalizedSlug] || null
}

/**
 * Get user's role constant from user object
 * Handles both user.role.slug and user.role_slug formats
 *
 * @param {Object} user - User object from auth service
 * @returns {string|null} ROLES constant or null
 *
 * @example
 * const { user } = useAuth()
 * const userRole = getUserRole(user)
 */
export const getUserRole = (user) => {
	if (!user) {
		return null
	}

	// Try user.role.slug first (auth service format)
	if (user.role && user.role.slug) {
		return mapRoleSlugToConstant(user.role.slug)
	}

	// Fall back to user.role_slug (direct API format)
	if (user.role_slug) {
		return mapRoleSlugToConstant(user.role_slug)
	}

	return null
}

/**
 * Check if user has one of the allowed roles
 *
 * @param {Object} user - User object
 * @param {string[]} allowedRoles - Array of ROLES constants
 * @returns {boolean} True if user has any of the allowed roles
 *
 * @example
 * const canAccess = hasAnyRole(user, [ROLES.COMPLIANCE_MANAGER, ROLES.PROJECT_MANAGER])
 */
export const hasAnyRole = (user, allowedRoles = []) => {
	const userRole = getUserRole(user)
	if (!userRole) return false

	return allowedRoles.includes(userRole)
}

export default {
	mapRoleSlugToConstant,
	getUserRole,
	hasAnyRole,
}
